import type { ReactNode } from 'react';

import type { FormField } from '../schemas.js';

import { CepField } from './fields/CepField.js';
import { CnpjField } from './fields/CnpjField.js';
import { EmailField } from './fields/EmailField.js';
import { MultiselectField } from './fields/MultiselectField.js';
import { RadioField } from './fields/RadioField.js';
import { SelectField } from './fields/SelectField.js';
import { TelField } from './fields/TelField.js';
import { TextField } from './fields/TextField.js';
import { TextareaField } from './fields/TextareaField.js';
import { isVisible } from './logic/visibleIf.js';
import type { CepAutofillData, CnpjAutofillData } from './hooks/useAutofillMapping.js';

export interface FormFieldRendererProps {
  field: FormField;
  answers: Record<string, unknown>;
  errors: Record<string, string>;
  onChangeField: (fieldId: string, value: unknown) => void;
  onCnpjAutofill: (data: CnpjAutofillData) => void;
  onCepAutofill: (data: CepAutofillData) => void;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Renderiza **um `FormField`** com o componente correto por tipo
 * (`switch` pelos 8 tipos suportados) + follow_up/other_field
 * aninhados quando suas condições `visible_if` batem.
 *
 * Extraído do `FormRenderer` para respeitar o limite de 200 linhas
 * por componente (AGENTS.md §5.1). A decisão de renderizar ou não
 * (`isVisible`) fica no próprio componente — retorna `null` quando
 * o campo está oculto, simplificando o loop do renderer principal.
 *
 * **Recursão**: renderiza a si mesmo para `follow_up`/`other_field`
 * (profundidade prática: 1 nível; schema aceita mais mas renderer
 * só trata o primeiro nível de aninhamento).
 */
export function FormFieldRenderer({
  field,
  answers,
  errors,
  onChangeField,
  onCnpjAutofill,
  onCepAutofill,
  disabled,
  idPrefix,
}: FormFieldRendererProps): ReactNode {
  if (!isVisible(field.visible_if, answers)) return null;

  const rawValue = answers[field.id];
  const err = errors[field.id];

  let main: ReactNode;
  switch (field.type) {
    case 'multiselect': {
      // Multiselect recebe array; normalização defensiva no próprio
      // componente (aceita string caso prefill venha vazio).
      const msValue = Array.isArray(rawValue) ? (rawValue as string[]) : [];
      main = (
        <MultiselectField
          field={field}
          value={msValue}
          onChange={(next) => onChangeField(field.id, next)}
          error={err}
          disabled={disabled}
          idPrefix={idPrefix}
        />
      );
      break;
    }
    default: {
      const value = typeof rawValue === 'string' ? rawValue : '';
      const common = {
        field,
        value,
        onChange: (next: string) => onChangeField(field.id, next),
        error: err,
        disabled,
        idPrefix,
      };
      switch (field.type) {
        case 'text':
          main = <TextField {...common} />;
          break;
        case 'textarea':
          main = <TextareaField {...common} />;
          break;
        case 'email':
          main = <EmailField {...common} />;
          break;
        case 'tel':
          main = <TelField {...common} />;
          break;
        case 'cnpj':
          main = <CnpjField {...common} onAutofill={onCnpjAutofill} />;
          break;
        case 'cep':
          main = <CepField {...common} onAutofill={onCepAutofill} />;
          break;
        case 'select':
          main = <SelectField {...common} />;
          break;
        case 'radio':
          main = <RadioField {...common} />;
          break;
      }
    }
  }

  const followUp = field.follow_up ? (
    <FormFieldRenderer
      field={field.follow_up}
      answers={answers}
      errors={errors}
      onChangeField={onChangeField}
      onCnpjAutofill={onCnpjAutofill}
      onCepAutofill={onCepAutofill}
      disabled={disabled}
      idPrefix={idPrefix}
    />
  ) : null;

  const otherField = field.other_field ? (
    <FormFieldRenderer
      field={field.other_field}
      answers={answers}
      errors={errors}
      onChangeField={onChangeField}
      onCnpjAutofill={onCnpjAutofill}
      onCepAutofill={onCepAutofill}
      disabled={disabled}
      idPrefix={idPrefix}
    />
  ) : null;

  return (
    <div>
      {main}
      {followUp || otherField ? (
        <div className="mt-4 space-y-5 rounded-md border-l-2 border-border pl-4">
          {followUp}
          {otherField}
        </div>
      ) : null}
    </div>
  );
}
